import styled from "styled-components";

interface IPingAnimationProps {
  count: number;
  position?: string;
  /** "warning" | "brand" — anything else falls back to the red ping tokens. */
  pingType?: string;
  /** "sm" shrinks the badge for inline use; omit for the default 20px. */
  size?: "sm";
}

const DIAMETER = { default: 20, sm: 16 };
const FONT_SIZE = { default: 13, sm: 11 };

const diameter = (size: IPingAnimationProps["size"]) =>
  `${size === "sm" ? DIAMETER.sm : DIAMETER.default}px`;

const fontSize = (size: IPingAnimationProps["size"]) =>
  `${size === "sm" ? FONT_SIZE.sm : FONT_SIZE.default}px`;

// "brand" uses the brand palette; the other two keep the existing --ping-* vars,
// which have no brand equivalent.
const pingBackground = (pingType: string | undefined) => {
  if (pingType === "warning") return "var(--ping-background-color-warning)";
  if (pingType === "brand") return "var(--brand-purple-600)";
  return "var(--ping-background-color)";
};

const pingText = (pingType: string | undefined) => {
  if (pingType === "warning") return "var(--ping-text-color-warning)";
  if (pingType === "brand") return "var(--brand-white)";
  return "var(--ping-text-color)";
};

/* Must always be positioned: the ring inside is absolute, so a static container
   hands its containing block to whatever positioned ancestor happens to be
   above (an OverlayPanel, a modal), and the ring then neither scrolls with the
   badge nor gets clipped by the badge's scroll container. `relative` is the
   default; callers that want the badge to hang off a sibling icon pass
   "absolute", which is the only case the corner offsets belong to. */
const StyledPingContainer = styled.span<{
  position: string | undefined;
  size: IPingAnimationProps["size"];
}>`
  position: ${(props) => props.position || "relative"};
  ${(props) =>
    props.position === "absolute"
      ? `
  top: -58%;
  right: -40%;
  `
      : ""}
  min-width: ${(props) => diameter(props.size)} !important;
  min-height: ${(props) => diameter(props.size)} !important;
  flex-shrink: 0;
  display: flex;
  border-radius: 100%;
`;

const StyledPing = styled.span<{
  pingType: string | undefined;
  size: IPingAnimationProps["size"];
}>`
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  position: absolute;
  inset: 0;
  display: inline-flex;
  border-radius: 100%;
  min-width: ${(props) => diameter(props.size)};
  min-height: ${(props) => diameter(props.size)};
  color: transparent;
  /* Scales to 2x, so it would otherwise swallow clicks aimed at neighbours. */
  pointer-events: none;
  background-color: ${(props) => pingBackground(props.pingType)};
  opacity: 75%;
  ::after {
    display: block;
    content: "";
    padding-bottom: 100%;
  }
  @keyframes ping {
    75%,
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`;

const StyledRound = styled.span<{
  pingType: string | undefined;
  size: IPingAnimationProps["size"];
}>`
  border-radius: 100%;
  min-width: ${(props) => diameter(props.size)};
  min-height: ${(props) => diameter(props.size)};
  padding: 2px;
  background-color: ${(props) => pingBackground(props.pingType)};
  color: ${(props) => pingText(props.pingType)};
  /* Positioned so the solid badge paints above the ring — an unpositioned
     sibling loses the paint order to it and the count flickers under the
     translucent pulse. */
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${(props) => fontSize(props.size)};
  ::after {
    display: block;
    content: "";
    padding-bottom: 100%;
  }
`;

function PingAnimation({
  count,
  position,
  pingType,
  size,
}: IPingAnimationProps) {
  return (
    <StyledPingContainer position={position} size={size}>
      <StyledPing pingType={pingType} size={size}>
        {count}
      </StyledPing>
      <StyledRound pingType={pingType} size={size}>
        {count}
      </StyledRound>
    </StyledPingContainer>
  );
}

export default PingAnimation;
